import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addItemToWishlist,
  isWishlistItemExist,
  removeWishlistItem
} from "../../State/Wishlist/Action";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import AuthModal from "../../Auth/AuthModal";
import { getUser } from "../../State/Auth/Action";
import { addItemToCart, buyNowItem } from "../../State/Cart/Action";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import defaultImage from "../../../Common/productDefaultImage.png";
import { toast } from "react-toastify";

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");
  const role = localStorage.getItem("role");
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const wishlistLoading = useSelector((state) => state.wishlist.loading);
  const [slideIndex, setSlideIndex] = useState(0);
  const { convert, customerPriceWithCharge, payableAmount, selectedCurrency } = useConvertPrice();
  const [loadingProductId, setLoadingProductId] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [cartBusy, setCartBusy] = useState(false);

  const handleOpenAuthModal = () => setOpenAuthModal(true);
  const handleCloseAuthModal = () => setOpenAuthModal(false);

  useEffect(() => {
    if (jwt && role === "CUSTOMER") {
      dispatch(getUser(jwt));
    }
  }, [jwt, dispatch, role]);

  useEffect(() => {
    if (jwt && role === "CUSTOMER" && product.id) {
      dispatch(isWishlistItemExist(product.id)).then((exists) => {
        setIsInWishlist(exists);
      });
    }
  }, [dispatch, product.id, jwt, role]);

  const handleFavorite = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const productId = product.id;
    setLoadingProductId(productId);
    try {
      if (!jwt || role !== "CUSTOMER") {
        sessionStorage.setItem("pendingWishlistProduct", productId);
        handleOpenAuthModal();
        return;
      }
      if (isInWishlist) {
        await dispatch(removeWishlistItem(productId));
        setIsInWishlist(false);
      } else {
        await dispatch(addItemToWishlist(productId));
        setIsInWishlist(true);
      }
    } finally {
      setLoadingProductId(null);
    }
  };

  useEffect(() => {
    const pendingProductId = sessionStorage.getItem("pendingWishlistProduct");
    if (pendingProductId && jwt && role === "CUSTOMER") {
      dispatch(addItemToWishlist(pendingProductId));
      setIsInWishlist(true);
      sessionStorage.removeItem("pendingWishlistProduct");
    }
  }, [jwt, role, dispatch]);

  const primaryImages = useMemo(() => {
    return product.images?.filter((img) => img.isPrimary) || [];
  }, [product.images]);

  useEffect(() => {
    if (primaryImages.length > 1) {
      const interval = setInterval(() => {
        setSlideIndex((prev) => (prev + 1) % primaryImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [primaryImages]);

  const currentImage = primaryImages.length > 0
    ? primaryImages[slideIndex]?.fullUrl
    : defaultImage;

  const sizes = useMemo(() => {
    const seen = new Set();
    return (product.variants || [])
      .map((variant) => variant.size?.name)
      .filter((name) => {
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      });
  }, [product.variants]);

  useEffect(() => {
    if (sizes.length === 1) {
      setSelectedSize(sizes[0]);
    }
  }, [sizes]);

  const resolveVariant = () => {
    const variants = product.variants || [];
    if (selectedSize) {
      const inStock = variants.find(
        (variant) =>
          variant.size?.name === selectedSize &&
          (variant.stockQuantity == null || variant.stockQuantity > 0)
      );
      return inStock || variants.find((variant) => variant.size?.name === selectedSize);
    }
    if (sizes.length > 0) return null;
    return variants.reduce(
      (min, curr) => (curr?.adminPrice < min?.adminPrice ? curr : min),
      variants[0]
    );
  };

  const handleCartAction = async (e, mode) => {
    e.stopPropagation();
    e.preventDefault();

    if (!jwt || role !== "CUSTOMER") {
      sessionStorage.setItem(
        "pendingListingCart",
        JSON.stringify({ productId: product.id, size: selectedSize, mode })
      );
      handleOpenAuthModal();
      return;
    }

    if (sizes.length > 0 && !selectedSize) {
      toast.error("Please select a size");
      return;
    }

    const variant = resolveVariant();
    if (!variant?.id) {
      toast.error("Please select a size");
      return;
    }
    if (variant.stockQuantity != null && variant.stockQuantity < 1) {
      toast.error("Selected size is out of stock.");
      return;
    }

    const color = variant.productColor?.color?.name;
    const data = {
      size: selectedSize || variant.size?.name,
      color,
      quantity: 1,
      customerPriceWithCharge: variant.customerPrice,
      selectedCurrency,
    };

    if (!data.size || !data.color) {
      navigate(`/product/${product.id}`);
      return;
    }

    setCartBusy(true);
    try {
      const submitItem = mode === "buyNow" ? buyNowItem : addItemToCart;
      await dispatch(submitItem(product.id, variant.id, data, navigate));
    } finally {
      setCartBusy(false);
    }
  };

  useEffect(() => {
    const pending = sessionStorage.getItem("pendingListingCart");
    if (!pending || !jwt || role !== "CUSTOMER") return;
    const parsed = JSON.parse(pending);
    if (parsed.productId !== product.id) return;
    sessionStorage.removeItem("pendingListingCart");
    if (parsed.size) setSelectedSize(parsed.size);

    const variants = product.variants || [];
    const variant = parsed.size
      ? variants.find((item) => item.size?.name === parsed.size)
      : variants[0];
    const color = variant?.productColor?.color?.name;
    if (!variant?.id || !color) return;

    const submitItem = parsed.mode === "buyNow" ? buyNowItem : addItemToCart;
    dispatch(
      submitItem(
        product.id,
        variant.id,
        {
          size: parsed.size || variant.size?.name,
          color,
          quantity: 1,
          customerPriceWithCharge: variant.customerPrice,
          selectedCurrency,
        },
        navigate
      )
    );
  }, [jwt, role, product.id, product.variants, dispatch, navigate, selectedCurrency]);

  return (
    product.id && (
      <>
        <div
          className="w-full cursor-pointer"
          onClick={() => navigate(`/product/${product.id}`)}
        >
          <div className="group flex flex-col bg-transparent">
            <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-neutral-100">
              <img
                src={currentImage}
                alt={product.title}
                className="object-cover w-full h-full"
                loading="lazy"
              />
              <button
                onClick={handleFavorite}
                className="group/heart absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/95 shadow-sm flex items-center justify-center border-0"
                disabled={loadingProductId === product.id || wishlistLoading}
              >
                {loadingProductId === product.id && wishlistLoading ? (
                  <span className="inline-block w-4 h-4 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" />
                ) : isInWishlist ? (
                  <Favorite className="text-red-500" style={{ fontSize: 18 }} />
                ) : (
                  <FavoriteBorder className="text-stone-400 group-hover/heart:text-red-500" style={{ fontSize: 18 }} />
                )}
              </button>

              <div className="absolute inset-x-0 bottom-0 hidden group-hover:flex flex-col items-center px-3 pb-3 pt-10 bg-gradient-to-t from-black/55 via-black/20 to-transparent">
                {sizes.length > 0 && (
                  <>
                    <p className="mb-2 font-sans text-[11px] tracking-[0.16em] uppercase text-white">
                      Select size
                    </p>
                    <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                      {sizes.slice(0, 6).map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize(size);
                          }}
                          className={`min-w-[32px] h-8 px-2 border text-[12px] ${
                            selectedSize === size
                              ? "bg-white text-[#333] border-white"
                              : "bg-transparent text-white border-white/80"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="flex w-full gap-1.5">
                  <button
                    type="button"
                    disabled={cartBusy}
                    onClick={(e) => handleCartAction(e, "cart")}
                    className="flex-1 bg-white text-[#333] text-[10px] tracking-[0.1em] uppercase py-2 border-0 font-semibold disabled:opacity-60"
                  >
                    Add to cart
                  </button>
                  <button
                    type="button"
                    disabled={cartBusy}
                    onClick={(e) => handleCartAction(e, "buyNow")}
                    className="flex-1 bg-white text-[#333] text-[10px] tracking-[0.1em] uppercase py-2 border-0 font-semibold disabled:opacity-60"
                  >
                    Buy now
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col pt-3 px-0.5 text-left">
              <h3 className="font-display italic text-[14px] leading-5 font-normal text-[#333333] truncate">
                {product?.brand?.name || "Brand"}
              </h3>
              <p className="mt-0.5 font-sans text-[13px] leading-5 font-light text-[#707070] line-clamp-1">
                {product?.title || ""}
              </p>

              {(() => {
                const cheapestVariant = product?.variants?.reduce(
                  (min, curr) =>
                    curr?.adminPrice < min?.adminPrice ? curr : min,
                  product?.variants?.[0]
                );
                if (!cheapestVariant) return null;
                const hasDiscount = cheapestVariant.viewTotalDiscount > 0;
                const originalPrice = convert(
                  customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount)
                );
                const discountedPrice = payableAmount(
                  customerPriceWithCharge(selectedCurrency, cheapestVariant.viewCustomerAmount),
                  cheapestVariant.viewTotalDiscount
                );
                return (
                  <div className="flex flex-wrap items-baseline gap-x-2 mt-1 font-sans">
                    {hasDiscount ? (
                      <>
                        <span className="text-[14px] leading-5 font-semibold text-[#333333]">
                          {discountedPrice} {selectedCurrency}
                        </span>
                        <span className="text-[13px] text-stone-400 line-through">
                          {originalPrice}
                        </span>
                        <span className="text-[13px] font-medium text-[#2f9e44]">
                          {cheapestVariant.viewTotalDiscount}% OFF
                        </span>
                      </>
                    ) : (
                      <span className="text-[14px] leading-5 font-semibold text-[#333333]">
                        {originalPrice} {selectedCurrency}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <AuthModal handleClose={handleCloseAuthModal} open={openAuthModal} />
      </>
    )
  );
};

export default ProductCard;
